import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';
import uniqWith from 'lodash/uniqWith';

import {ResourceType} from 'common/report/constants';
import {
  getBehaviorTrackingProps,
  isEventBasedBehavioralSegmentation,
} from 'common/query-builder/query-entry-section/row-types/behavior/utils';

import {propertyItemToSegmentation, segmentationToPropertyItem} from '../properties-menu/util';
import * as PropertiesMenu from '../properties-menu/types';
import {getCohortFromPropertyItem} from '../utils/util';
import {
  CohortSegment,
  getSegmentValueResourceType,
  isCohortSegment,
  isEventSegment,
  isInlineCohortSegment,
  isSavedCohortSegment,
  isUserSegment,
  OtherSegment,
  SavedCohortSegment,
  Segment,
  SegmentValue,
} from './types';

export function isGroupByEqual(groupByA, groupByB, _?: boolean) {
  return (
    groupByA.length === groupByB.length &&
    groupByA.every((aGrouping, groupIndex) => {
      const bGrouping = groupByB[groupIndex];
      if (!Array.isArray(aGrouping)) {
        return isEqual(aGrouping, bGrouping);
      } else if (!Array.isArray(bGrouping)) {
        return false;
      } else if (!isCohortSegment(aGrouping)) {
        return (
          aGrouping.length === bGrouping.length &&
          aGrouping.every((aItem, itemIndex) => isEqual(aItem, bGrouping[itemIndex]))
        );
      } else {
        const sortedAGrouping = sortBy(aGrouping as SavedCohortSegment[], (aItem) => aItem.cohort.id);
        const sortedBGrouping = sortBy(bGrouping as SavedCohortSegment[], (bItem) => bItem.cohort.id);
        return (
          sortedAGrouping.length === sortedBGrouping.length &&
          sortedAGrouping.every((aItem, itemIndex) =>
            isEqual(omit(aItem.cohort, [`name`]), omit(sortedBGrouping[itemIndex].cohort, [`name`])),
          )
        );
      }
    })
  );
}

/**
 * Transforms bookmark group by to UI format by
 * consolidating negated and non-negated cohorts
 */
export function bookmarkGroupByToGroupBy(groupBy: Segment[]): Segment[] {
  return groupBy.map((grouping) => {
    if (isCohortSegment(grouping)) {
      return uniqWith(grouping, (cohortA, cohortB) => {
        // compare persisted cohort ids
        if (isSavedCohortSegment(cohortA) && isSavedCohortSegment(cohortB)) {
          return cohortA.cohort.id === cohortB.cohort.id;
        }

        // compare raw cohort values
        if (isInlineCohortSegment(cohortA) && isInlineCohortSegment(cohortB)) {
          return isEqual(cohortA.cohort.raw_cohort, cohortB.cohort.raw_cohort);
        }

        return false;
      }).map((item) => {
        if (isInlineCohortSegment(item)) {
          return {cohort: {...item.cohort, name: item.cohort.raw_cohort.name ?? item.cohort.name}};
        }

        return item;
      });
    }
    return grouping;
  });
}

/**
 * Transforms UI format group by to bookmark group by
 */
export function groupByToBookmarkGroupBy(groupBy: Segment[], includeNegated = true): Segment[] {
  return groupBy.map((grouping) => {
    if (isCohortSegment(grouping)) {
      return (grouping as CohortSegment[]).flatMap(({cohort}) => {
        const group = [
          {
            [ResourceType.Cohort]: {
              ...cohort,
              negated: false,
            },
          } as CohortSegment,
          {
            [ResourceType.Cohort]: {
              ...cohort,
              negated: true,
            },
          } as CohortSegment,
        ];
        return includeNegated ? group : [group[0]];
      });
    }
    return grouping;
  });
}

export function getGroupByProperties(groupBy: Segment[]) {
  return (groupBy || [])
    .filter((segment) => !isCohortSegment(segment) && (isUserSegment(segment) || isEventSegment(segment)))
    .map((segment) => {
      if (isEventSegment(segment)) {
        return segment.event;
      } else if (isUserSegment(segment)) {
        return segment.user;
      }
      return null;
    });
}

export function otherPropertyItemToSegment(propertyItem: PropertiesMenu.Item): OtherSegment {
  return {
    [ResourceType.Other]: {
      ...propertyItemToSegmentation(propertyItem, ResourceType.Other),
      value: propertyItem.value as string,
      label: propertyItem.label,
    },
  };
}

export function propertySelectionToSegmentation(selection: PropertiesMenu.Selection): SegmentValue {
  const source = Array.isArray(selection) ? selection[0]?.resourceType : selection.resourceType;

  if (Array.isArray(selection) && selection.length === 0) {
    return [];
  }

  // multi-seg from the properties menu is only supported for Cohorts
  // this will change in the future with the value comparison project
  if (Array.isArray(selection) && source && source !== ResourceType.Cohort) {
    throw new Error(`unsupported multi-seg values`);
  }

  if (source === ResourceType.Cohort) {
    selection = Array.isArray(selection) ? selection : [selection];
    return selection.map((item) => getCohortFromPropertyItem(item));
  }

  return propertyItemToSegmentation(selection, source);
}

export function segmentationToPropertySelection(selection: SegmentValue): PropertiesMenu.Selection | null {
  if (!selection) {
    return null;
  }

  if (isCohortSegment(selection)) {
    return selection.map((entry: CohortSegment) => {
      if (isInlineCohortSegment(entry)) {
        return {
          resourceType: ResourceType.Cohort,
          label: entry.cohort.name,
          value: entry,
        };
      } else {
        return {
          resourceType: ResourceType.Cohort,
          label: entry.cohort.name,
          value: entry.cohort.id,
        };
      }
    });
  }

  // TODO bucc - do we ever need to convert an entire value comparison to a prop selection?
  if (Array.isArray(selection)) {
    return null;
  }

  return segmentationToPropertyItem(selection);
}

export function getSegmentTrackingProps(property: SegmentValue) {
  let extraProps = {};
  let segmentName: string | string[] = ``;
  let isCustomPropertySegment = false;
  const source = getSegmentValueResourceType(property);

  if (isCohortSegment(property)) {
    segmentName = `cohorts`;
  } else {
    // Handle types for when Segmentation[], then set property to the first property for basic segment props
    const singularProperty = Array.isArray(property) ? property[0] : property;
    if (Array.isArray(property)) {
      extraProps = {
        'Filter operator': property.map((f) => f.filterOperator),
        'Filter value': property.map((f) => f.filterValue),
      };
    }
    switch (source) {
      case ResourceType.User:
      case ResourceType.Event: {
        const isEventBasedBehavior = isEventBasedBehavioralSegmentation(singularProperty);
        segmentName = singularProperty.propertyName;
        isCustomPropertySegment = !!singularProperty.customProperty || !!singularProperty.customPropertyId;
        extraProps = {
          ...extraProps,
          ...(isEventBasedBehavior ? getBehaviorTrackingProps(singularProperty.behavior) : {}),
          'Is behavior': isEventBasedBehavior,
          'Is value comparison': Array.isArray(property),
          'Property data type': singularProperty.propertyType,
          'Property default type': singularProperty.propertyDefaultType,
        };
        break;
      }
      case ResourceType.Other: {
        const propertyName = singularProperty.propertyName.toString();
        segmentName = `Other: ${propertyName}`;
        break;
      }
      default:
        break;
    }
  }

  return {
    ...extraProps,
    'Segment type': source,
    'Segment name': segmentName,
    'Segments length': Array.isArray(property) ? property.length : 1,
    'Is segment custom property': isCustomPropertySegment,
  };
}
